import { isString } from "@sinch/feature-flags";
import { useAtomValue } from "jotai";
import { DataCollectors, type AnalyticsUser } from "../../../analytics/types";
import {
  useGroupRudderstackUser,
  useIdentifyRudderstackUser,
} from "../../../analytics/useHandleRudderstackUser";
import { useInitRudderstackSession } from "../../../analytics/useInitRudderstackSession";
import { useLoginEvent } from "../../../analytics/useLoginEvent";
import {
  useSetEventListener,
  useSetUserlessEventListener,
} from "../../../analytics/useSetEventListener";
import { usePartner } from "../../../api/hooks/usePartner";
import { useUser } from "../../../api/hooks/useUser";
import type { ShellUser } from "../../../api/types";
import {
  isSomeCookieConsentGranted,
  useIsConsentRequired,
} from "../../../components/CookieBanner/consent";
import { onesaasConsentAtom } from "../../../components/CookieBanner/onesaas-consent-cookie";
import { safeRetrieveValue } from "../../../helpers/safeRetrieveValue";
import { usePageViewEventGenerator } from "../../../hooks/usePageViewEventGenerator";

const DATA_COLLECTORS = [
  DataCollectors.RudderStack,
  DataCollectors.GA4,
  DataCollectors.Datadog,
];

export const usePublicRouteAnalytics = () => {
  const isListenerSet = useSetUserlessEventListener(DATA_COLLECTORS);
  usePageViewEventGenerator(isListenerSet);
};

const getRole = (roles: string[]) => {
  if (roles.indexOf("admin") >= 0) return "Administrator";
  if (roles.indexOf("billing") >= 0) return "Billing";
  if (roles.indexOf("user") >= 0) return "Advanced";
  if (roles.indexOf("basic") >= 0) return "Basic";
  return "";
};

const useIdentifyMMUser = (
  isSessionStarted: boolean,
  user: ShellUser | null
) => {
  const userId = user?.id;
  const userRole = getRole(user?.roles ?? []);

  useIdentifyRudderstackUser(isSessionStarted, userId, { User_role: userRole });
};

const useGroupMMUser = (isSessionStarted: boolean, user: ShellUser | null) => {
  const [partner] = usePartner(!!user);
  const accountId = user?.account?.id;

  const groupData = {
    billingType:
      safeRetrieveValue(user?.settings ?? {}, "billingType", isString) ?? "",
    country: user?.account?.country ?? "",
    group_type: "account",
    partnerName: partner?.accountName ?? "",
    vendorId:
      safeRetrieveValue(user?.settings ?? {}, "vendorId", isString) ??
      undefined,
    verificationStatus:
      safeRetrieveValue(user?.settings ?? {}, "verificationStatus", isString) ??
      undefined,
  };

  useGroupRudderstackUser(isSessionStarted, accountId, groupData);
};

const getMmRudderstackEventUser = (user: ShellUser | null): AnalyticsUser => {
  const vendorId =
    safeRetrieveValue(user?.settings || {}, "vendorId", isString) ?? "";
  const accountVendorId =
    vendorId && user?.account?.id ? `${vendorId}:${user.account.id}` : "";

  const defaultCountryCode = safeRetrieveValue(
    user?.settings || {},
    "defaultCountryCode",
    isString
  );

  const paymentType = safeRetrieveValue(
    user?.settings ?? {},
    "paymentType",
    isString
  );
  const billingType = safeRetrieveValue(
    user?.settings ?? {},
    "billingType",
    isString
  );
  const verificationStatus = safeRetrieveValue(
    user?.settings ?? {},
    "verificationStatus",
    isString
  );
  const accountCreatedAt = safeRetrieveValue(
    user?.settings ?? {},
    "accountCreatedAt",
    isString
  );

  return {
    ...user,
    account: {
      ...user?.account,
      id: user?.account?.id ?? "",
      vendorId: accountVendorId ?? "",
      country: user?.account?.country ?? defaultCountryCode ?? "",
      paymentType: paymentType ?? "",
      billingType: billingType ?? "",
      verificationStatus: verificationStatus ?? "",
      createdAt: accountCreatedAt ?? "",
    },
    app: {
      vendorId,
    },
    createdAt: user?.createdAt ?? "",
  };
};

export const useAnalytics = () => {
  const [user] = useUser();
  const onesaasConsent = useAtomValue(onesaasConsentAtom);
  const shouldHandleConsent = useIsConsentRequired();

  const isSessionStarted = useInitRudderstackSession(
    !shouldHandleConsent || isSomeCookieConsentGranted(onesaasConsent)
  );

  useIdentifyMMUser(isSessionStarted, user);
  useGroupMMUser(isSessionStarted, user);
  user;

  const isListenerSet = useSetEventListener(
    getMmRudderstackEventUser(user),
    true,
    DATA_COLLECTORS
  );
  useLoginEvent(isListenerSet);
  usePageViewEventGenerator(isListenerSet);
};
